import { useEffect, useMemo, useState } from 'react'
import { AbstractConnector } from '@web3-react/abstract-connector'
import {
  Eip6963ProviderDetail,
  startEip6963Discovery,
  subscribeToEip6963,
} from '../connectors/eip6963'
import { Eip6963Connector } from '../connectors/Eip6963Connector'
import {
  SUPPORTED_CHAIN_IDS,
  bscConnector,
  injected,
  isWalletConnectConfigured,
  walletlink,
} from '../connectors'

export interface WalletOption {
  /** Stable identity, used as the React key and for the "last used wallet" memory. */
  id: string
  name: string
  /** Data/HTTP URL supplied by the wallet itself via EIP-6963, when discovered. */
  iconUrl?: string
  connector?: AbstractConnector
  /** False when the option is shown but cannot currently be used. */
  available: boolean
  /** Why it is unavailable - surfaced in the UI instead of failing silently. */
  unavailableReason?: string
  /** True when the wallet was found installed in this browser via EIP-6963. */
  detected: boolean
}

/**
 * Wallets we want to appear at the top of the list when installed, in this order.
 * Anything else discovered via EIP-6963 is still listed, just after these.
 * Keys are EIP-6963 `rdns` identifiers.
 */
const PREFERRED_RDNS_ORDER = [
  'io.metamask',
  'com.trustwallet.app',
  'com.binance.wallet',
  'com.okex.wallet',
  'io.rabby',
  'com.coinbase.wallet',
  'com.safepal',
  'com.mathwallet',
  'pro.tokenpocket',
]

function rankOf(rdns: string): number {
  const index = PREFERRED_RDNS_ORDER.indexOf(rdns)
  return index === -1 ? PREFERRED_RDNS_ORDER.length : index
}

/**
 * Builds the wallet list shown in the connect modal.
 *
 * Injected wallets are discovered at runtime via EIP-6963 rather than hardcoded, so
 * the list reflects what the user actually has installed - and each entry connects to
 * that specific wallet instead of to a shared `window.ethereum` slot. Coinbase Wallet
 * (WalletLink) and Binance Chain Wallet use their own SDKs and are appended
 * explicitly. WalletConnect is listed but disabled until a Project ID is configured.
 */
export default function useWalletOptions(): WalletOption[] {
  const [detected, setDetected] = useState<Eip6963ProviderDetail[]>([])

  useEffect(() => {
    startEip6963Discovery()
    return subscribeToEip6963(setDetected)
  }, [])

  return useMemo(() => {
    const discovered: WalletOption[] = detected
      .slice()
      .sort((a, b) => {
        const delta = rankOf(a.info.rdns) - rankOf(b.info.rdns)
        return delta !== 0 ? delta : a.info.name.localeCompare(b.info.name)
      })
      .map((detail) => ({
        id: detail.info.rdns,
        name: detail.info.name,
        iconUrl: detail.info.icon,
        connector: new Eip6963Connector({ detail, supportedChainIds: SUPPORTED_CHAIN_IDS }),
        available: true,
        detected: true,
      }))

    const options: WalletOption[] = [...discovered]

    // Coinbase Wallet speaks WalletLink, not EIP-1193 injection, so it never shows up
    // in discovery and is always offered.
    if (!discovered.some((option) => option.id === 'com.coinbase.wallet')) {
      options.push({
        id: 'coinbase',
        name: 'Coinbase Wallet',
        connector: walletlink,
        available: true,
        detected: false,
      })
    }

    // Binance Chain Wallet's browser extension is end-of-life; the current product is
    // Binance Web3 Wallet, which announces over EIP-6963 and is picked up above. This
    // entry is only a fallback for users still on the legacy extension.
    if (!discovered.some((option) => option.id === 'com.binance.wallet')) {
      options.push({
        id: 'bsc',
        name: 'Binance Chain Wallet (legacy)',
        connector: bscConnector,
        available: true,
        detected: false,
      })
    }

    options.push({
      id: 'walletconnect',
      name: 'WalletConnect',
      connector: undefined,
      available: isWalletConnectConfigured,
      unavailableReason: isWalletConnectConfigured
        ? undefined
        : 'Not configured yet - needs a WalletConnect Cloud Project ID.',
      detected: false,
    })

    // Last resort: if no wallet announced itself but a legacy provider is present,
    // still give the user a way in rather than an empty modal.
    if (discovered.length === 0 && typeof window !== 'undefined' && (window as any).ethereum) {
      options.unshift({
        id: 'injected',
        name: 'Browser Wallet',
        connector: injected,
        available: true,
        detected: true,
      })
    }

    return options
  }, [detected])
}
